import { useEffect } from 'react'

import { Elements, RecurlyProvider, useRecurly } from 'lib/recurly'
import { translate } from 'utils/translation'

export type TreatErrorFunction = (props: {
  tokenId: string
  onContinue: (threeDToken: string) => void
  setError: (error: string) => void
  setShowThreeDAuth: (showThreeDAuth: boolean) => void
}) => void

interface ThreeDAuthBoxWithErrorTreatmentProps {
  setTreatError: (arg0: TreatErrorFunction) => void
  treatError: TreatErrorFunction
}

export const ThreeDSecureErrorHelper = ({
  setTreatError,
  treatError,
}: ThreeDAuthBoxWithErrorTreatmentProps) => {
  const recurly = useRecurly()

  useEffect(() => {
    let observer: MutationObserver | null = null
    const addObserver = () => {
      observer = new MutationObserver(() => {
        const iframe = document.querySelector(
          `#checkout-3d-auth-container > div > iframe`,
        )
        if (iframe && iframe.getAttribute('scrolling') === 'no') {
          iframe.setAttribute('scrolling', 'yes')
        }
      })

      const container = document.querySelector('#checkout-3d-auth-container')

      if (container) {
        observer.observe(container, {
          subtree: true,
          childList: true,
        })
      }
    }

    const removeObserver = () => {
      if (observer) {
        observer.disconnect()
        observer = null
      }
    }

    const treat3DSecureError = (
      params: {
        tokenId: string
        onContinue: (threeDToken: string) => void
        setError: (error: string) => void
        setShowThreeDAuth: (showThreeDAuth: boolean) => void
      } | null,
    ) => {
      if (!params) {
        return
      }

      const { tokenId, onContinue, setError, setShowThreeDAuth } = params
      document.querySelector('#checkout-3d-auth-container')?.replaceChildren()
      setShowThreeDAuth(true)

      const risk = recurly.Risk()
      const threeDSecure = risk.ThreeDSecure({
        actionTokenId: tokenId,
      })

      threeDSecure.on('token', (threeDToken: string) => {
        removeObserver()
        setShowThreeDAuth(false)
        return onContinue(threeDToken)
      })

      threeDSecure.on('error', () => {
        removeObserver()
        setShowThreeDAuth(false)
        setError(translate('three_d_verification_failed'))
      })

      addObserver()

      threeDSecure.attach(document.querySelector('#checkout-3d-auth-container'))
    }
    if (!treatError) {
      setTreatError(treat3DSecureError)
    }

    return () => removeObserver()
  }, [recurly, setTreatError, treatError])

  return null
}

export const ThreeDSecureErrorHelperWithProviders = ({
  publicKey,
  setTreatError,
  treatError,
}: ThreeDAuthBoxWithErrorTreatmentProps & { publicKey: string }) => {
  return (
    <RecurlyProvider publicKey={publicKey}>
      <Elements>
        <ThreeDSecureErrorHelper
          setTreatError={setTreatError}
          treatError={treatError}
        />
      </Elements>
    </RecurlyProvider>
  )
}
